import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { isNative, getSavedNativePushToken, registerNativePush, APP_SCHEME, completeNativeGoogleSignIn } from "@/lib/native";
import { ensureIap, iapLogout } from "@/lib/iap";

// Wires native shell behaviour (splash, status bar, back button, deep links, push taps). No-op on web.
export default function NativeBridge() {
  const nav = useNavigate();
  const { user, refreshUser } = useAuth();

  useEffect(() => {
    if (!isNative()) return;
    const handles = [];
    (async () => {
      const [{ App }, { SplashScreen }, { StatusBar, Style }, { PushNotifications }] = await Promise.all([
        import("@capacitor/app"),
        import("@capacitor/splash-screen"),
        import("@capacitor/status-bar"),
        import("@capacitor/push-notifications"),
      ]);
      document.documentElement.classList.add("native-app");
      StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
      SplashScreen.hide().catch(() => {});
      handles.push(await App.addListener("backButton", ({ canGoBack }) => {
        if (canGoBack) window.history.back();
        else App.exitApp();
      }));
      handles.push(await App.addListener("appUrlOpen", async ({ url }) => {
        const m = (url || "").match(/session_id=([^&]+)/);
        if (!url.startsWith(`${APP_SCHEME}://`) || !m) return;
        try {
          await completeNativeGoogleSignIn(decodeURIComponent(m[1]));
          await refreshUser();
          toast.success("Signed in with Google");
          nav("/dashboard", { replace: true });
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Google sign-in failed. Please try again.");
        }
      }));
      handles.push(await PushNotifications.addListener("pushNotificationActionPerformed", ({ notification }) => {
        const url = notification?.data?.url;
        if (typeof url === "string" && url.startsWith("/")) nav(url);
      }));
    })();
    return () => handles.forEach((h) => h.remove());
  }, [nav, refreshUser]);

  useEffect(() => {
    if (!isNative()) return;
    if (!user) { iapLogout(); return; }
    ensureIap(user.id).catch(() => {});
    // Refresh the device token on each sign-in if the user already opted in.
    if (getSavedNativePushToken()) registerNativePush({ prompt: false }).catch(() => {});
  }, [user]);

  return null;
}
